<template>
    <div class="login-wrapper register-wrapper">
        <div class="login-container">
            <div class="login-card register-card">
                <div class="login-header">
                    <img src="/images/2digital-horis.png" alt="2Digital" class="login-logo" />
                    <h1 class="login-title">Загрузка файлов</h1>
                    <p class="login-subtitle">XLSX, до 20 МБ каждый</p>
                </div>

                <form class="login-form" @submit.prevent="startBatch">
                    <input
                        id="inbound-file"
                        ref="fileInput"
                        type="file"
                        class="dropzone-input"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple
                        @change="onPickerChange"
                    />

                    <button
                        type="button"
                        class="dropzone"
                        :class="{ 'dropzone--active': isDragging }"
                        @dragenter.prevent="onDragEnter"
                        @dragover.prevent="onDragOver"
                        @dragleave.prevent="onDragLeave"
                        @drop.prevent="onDrop"
                        @click="openPicker"
                    >
                        <svg class="dropzone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span class="dropzone-title">Перетащите XLSX или нажмите</span>
                        <span class="dropzone-hint">Можно несколько файлов сразу</span>
                    </button>

                    <p v-if="notice" class="queue-notice">{{ notice }}</p>

                    <ul v-if="items.length" class="file-list">
                        <li
                            v-for="item in items"
                            :key="item.id"
                            class="file-row"
                            :class="'file-row--' + item.status"
                        >
                            <div class="file-row-top">
                                <span class="file-row-name">{{ item.file.name }}</span>
                                <button
                                    v-if="item.status === 'ready'"
                                    type="button"
                                    class="file-row-remove"
                                    aria-label="Убрать файл"
                                    @click="removeItem(item.id)"
                                >
                                    ×
                                </button>
                            </div>
                            <div class="file-row-meta">{{ statusLabel(item) }}</div>
                            <div
                                v-if="item.status !== 'ready' && item.status !== 'done' && item.status !== 'error'"
                                class="progress"
                                :class="{ 'progress--pulse': item.status === 'queued' || item.status === 'processing' }"
                            >
                                <div class="progress-bar" :style="{ width: barWidth(item) }" />
                            </div>
                            <p v-if="item.message" class="file-row-message">{{ item.message }}</p>
                        </li>
                    </ul>

                    <button
                        type="submit"
                        class="login-button"
                        :disabled="!canSubmit"
                        :class="{ loading: isBusy }"
                    >
                        <span v-if="!isBusy">{{ submitLabel }}</span>
                        <span v-else class="button-loader"></span>
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 20;
const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export default {
    name: 'InboundUpload',
    data() {
        return {
            items: [],
            nextId: 1,
            isDragging: false,
            dragDepth: 0,
            isBusy: false,
            notice: '',
            csrf: document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        };
    },
    computed: {
        readyCount() {
            return this.items.filter((item) => item.status === 'ready').length;
        },
        canSubmit() {
            return this.readyCount > 0 && !this.isBusy;
        },
        submitLabel() {
            return this.readyCount > 1 ? `Загрузить (${this.readyCount})` : 'Загрузить';
        },
    },
    methods: {
        openPicker() {
            this.$refs.fileInput?.click();
        },
        onPickerChange(event) {
            this.addFiles(event.target.files);
            event.target.value = '';
        },
        onDragEnter() {
            this.dragDepth += 1;
            this.isDragging = true;
        },
        onDragOver() {
            this.isDragging = true;
        },
        onDragLeave() {
            this.dragDepth = Math.max(0, this.dragDepth - 1);
            if (this.dragDepth === 0) {
                this.isDragging = false;
            }
        },
        onDrop(event) {
            this.dragDepth = 0;
            this.isDragging = false;
            this.addFiles(event.dataTransfer?.files);
        },
        fileKey(file) {
            return `${file.name}|${file.size}|${file.lastModified}`;
        },
        isXlsx(file) {
            const name = file.name.toLowerCase();
            if (!name.endsWith('.xlsx')) {
                return false;
            }

            return file.type === ''
                || file.type === XLSX_TYPE
                || file.type === 'application/octet-stream'
                || file.type === 'application/zip'
                || file.type === 'application/vnd.ms-excel';
        },
        addFiles(fileList) {
            if (!fileList || fileList.length === 0) {
                return;
            }

            this.notice = '';
            const existing = new Set(this.items.map((item) => this.fileKey(item.file)));
            const notes = [];
            let skippedDupes = 0;
            let skippedCap = 0;

            for (const file of fileList) {
                if (this.items.length >= MAX_FILES) {
                    skippedCap += 1;
                    continue;
                }

                const key = this.fileKey(file);
                if (existing.has(key)) {
                    skippedDupes += 1;
                    continue;
                }

                if (!this.isXlsx(file)) {
                    notes.push(`${file.name}: только .xlsx`);
                    continue;
                }

                if (file.size > MAX_BYTES) {
                    notes.push(`${file.name}: больше 20 МБ`);
                    continue;
                }

                existing.add(key);
                this.items.push({
                    id: this.nextId++,
                    file,
                    status: 'ready',
                    percent: 0,
                    message: '',
                });
            }

            if (skippedDupes > 0) {
                notes.push(`Пропущены дубликаты: ${skippedDupes}`);
            }
            if (skippedCap > 0) {
                notes.push(`Максимум ${MAX_FILES} файлов за раз`);
            }
            this.notice = notes.join('. ');
        },
        removeItem(id) {
            this.items = this.items.filter((item) => !(item.id === id && item.status === 'ready'));
        },
        statusLabel(item) {
            switch (item.status) {
                case 'ready':
                    return this.formatSize(item.file.size);
                case 'queued':
                    return 'В очереди';
                case 'uploading':
                    return `Загрузка ${item.percent}%`;
                case 'processing':
                    return 'Обработка';
                case 'done':
                    return 'Готово';
                case 'error':
                    return 'Ошибка';
                default:
                    return '';
            }
        },
        barWidth(item) {
            if (item.status === 'queued' || item.status === 'processing') {
                return '40%';
            }

            return `${item.percent}%`;
        },
        formatSize(bytes) {
            if (bytes < 1024) {
                return `${bytes} Б`;
            }
            if (bytes < 1024 * 1024) {
                return `${Math.round(bytes / 1024)} КБ`;
            }

            return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
        },
        startBatch() {
            if (!this.canSubmit) {
                return;
            }

            this.notice = '';
            this.isBusy = true;

            for (const item of this.items) {
                if (item.status === 'ready') {
                    item.status = 'queued';
                    item.percent = 0;
                    item.message = '';
                }
            }

            this.pump();
        },
        pump() {
            if (this.items.some((item) => item.status === 'uploading' || item.status === 'processing')) {
                return;
            }

            const next = this.items.find((item) => item.status === 'queued');
            if (!next) {
                this.isBusy = false;
                return;
            }

            this.uploadItem(next);
        },
        uploadItem(item) {
            item.status = 'uploading';
            item.percent = 0;

            const formData = new FormData();
            formData.append('file', item.file);

            const xhr = new XMLHttpRequest();
            xhr.open('POST', '/inbound');
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            xhr.setRequestHeader('X-CSRF-TOKEN', this.csrf);
            xhr.timeout = 180000;

            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable || item.status !== 'uploading') {
                    return;
                }
                item.percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
            };

            xhr.upload.onload = () => {
                if (item.status === 'uploading') {
                    item.percent = 100;
                    item.status = 'processing';
                }
            };

            xhr.onload = () => {
                const data = this.parseResponse(xhr);
                if (xhr.status >= 200 && xhr.status < 300) {
                    item.status = 'done';
                    item.percent = 100;
                    item.message = this.successText(data);
                } else {
                    item.status = 'error';
                    item.message = this.errorText(data);
                }
                this.pump();
            };

            xhr.onerror = () => {
                item.status = 'error';
                item.message = 'Произошла ошибка. Попробуйте ещё раз.';
                this.pump();
            };

            xhr.ontimeout = () => {
                item.status = 'error';
                item.message = 'Превышено время ожидания';
                this.pump();
            };

            xhr.send(formData);
        },
        parseResponse(xhr) {
            try {
                return JSON.parse(xhr.responseText || '{}');
            } catch {
                return {};
            }
        },
        successText(data) {
            const unmatched = Array.isArray(data.unmatched_billboard_ids)
                ? data.unmatched_billboard_ids.length
                : 0;
            let message = `Данные сохранены: ${data.upserted ?? 0} строк`;
            if (unmatched > 0) {
                message += `. Без кадра: ${unmatched}`;
            }
            if (data.warning) {
                message += `. ${data.warning}`;
            }

            return message;
        },
        errorText(data) {
            if (data.errors) {
                const errors = Object.values(data.errors).flat();
                return errors[0] || 'Ошибка загрузки';
            }

            return data.message || 'Ошибка загрузки';
        },
    },
};
</script>

<style scoped>
.dropzone-input {
    display: none;
}

.dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0;
    padding: 1.5rem 1rem;
    border: 1.5px dashed #cbd5e1;
    border-radius: 10px;
    background: #fff;
    color: inherit;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.2s, background 0.2s;
}

.dropzone:hover,
.dropzone--active {
    border-color: #94a3b8;
    background: #f8fafc;
}

.dropzone-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 0.5rem;
    color: #64748b;
}

.dropzone-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #334155;
}

.dropzone-hint {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #94a3b8;
}

.queue-notice {
    margin: 10px 0 0;
    font-size: 13px;
    color: #b45309;
    line-height: 1.4;
}

.file-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.file-row {
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
}

.file-row-top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.file-row-name {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
    word-break: break-all;
    line-height: 1.35;
}

.file-row-remove {
    flex-shrink: 0;
    margin: -2px -4px 0 0;
    padding: 0 6px;
    border: 0;
    background: transparent;
    color: #94a3b8;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
}

.file-row-remove:hover {
    color: #475569;
}

.file-row-meta {
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
}

.file-row--done .file-row-meta {
    color: #15803d;
}

.file-row--error .file-row-meta {
    color: #b91c1c;
}

.file-row-message {
    margin: 6px 0 0;
    font-size: 12px;
    color: #475569;
    line-height: 1.4;
    word-break: break-word;
}

.file-row--error .file-row-message {
    color: #b91c1c;
}

.progress {
    margin-top: 8px;
    height: 3px;
    overflow: hidden;
    border-radius: 99px;
    background: #e2e8f0;
}

.progress-bar {
    height: 100%;
    border-radius: 99px;
    background: #2563eb;
    transition: width 0.15s ease-out;
}

.progress--pulse .progress-bar {
    animation: inbound-pulse 1.1s ease-in-out infinite;
}

@keyframes inbound-pulse {
    0%,
    100% {
        opacity: 0.35;
        transform: translateX(-20%);
    }
    50% {
        opacity: 1;
        transform: translateX(80%);
    }
}
</style>
